/** Typed wrappers over the API routes. One function per endpoint. */

import { api } from "./client";
import type {
  CreatedPlan,
  DeletionStatus,
  DeloadStatus,
  Exercise,
  Kettlebell,
  MuscleFatigue,
  Paged,
  PlannedSession,
  Preferences,
  Readiness,
  SessionFeedback,
  SessionFocus,
  SessionType,
  Tag,
  TrainingSession,
  User,
} from "./types";

// --- Profile -------------------------------------------------------------

export const getMe = () => api.get<User>("/api/v1/me");

export const updateProfile = (body: {
  displayName?: string | null;
  timezone?: string;
  weightUnit?: "kg" | "lb";
}) => api.put<User>("/api/v1/me", body);

export const getPreferences = () => api.get<Preferences>("/api/v1/me/preferences");

export const updatePreferences = (body: Partial<Preferences>) =>
  api.put<Preferences>("/api/v1/me/preferences", body);

export const completeOnboarding = (body: {
  termsVersion: string;
  healthAckVersion: string;
  ageConfirmed: true;
}) => api.post<User>("/api/v1/me/onboarding", body);

// --- Training status -----------------------------------------------------

export const getFatigue = () =>
  api.get<{ muscleGroups: MuscleFatigue[] }>("/api/v1/me/fatigue");

export const getDeload = () => api.get<DeloadStatus>("/api/v1/me/deload");

export const getReadiness = () => api.get<Readiness>("/api/v1/me/readiness");

// --- GDPR ----------------------------------------------------------------

export const getDeletionStatus = () => api.get<DeletionStatus>("/api/v1/me/deletion");

export const requestDeletion = () => api.post<DeletionStatus>("/api/v1/me/deletion");

export const cancelDeletion = () => api.post<DeletionStatus>("/api/v1/me/deletion/cancel");

export const exportData = () => api.post<unknown>("/api/v1/me/export");

// --- Catalogue -----------------------------------------------------------

export const listKettlebells = () => api.get<Paged<Kettlebell>>("/api/v1/kettlebells");

export const addKettlebell = (weightKg: number, quantity = 1) =>
  api.post<Kettlebell>("/api/v1/kettlebells", { weightKg, quantity });

export const deleteKettlebell = (id: string) =>
  api.delete<{ message: string }>(`/api/v1/kettlebells/${id}`);

export const listExercises = () => api.get<Paged<Exercise>>("/api/v1/exercises");

export const getExercise = (id: string) => api.get<Exercise>(`/api/v1/exercises/${id}`);

export const listTags = (appliesTo?: string) =>
  api.get<Paged<Tag>>(
    appliesTo ? `/api/v1/tags?appliesTo=${encodeURIComponent(appliesTo)}` : "/api/v1/tags",
  );

// --- Sessions ------------------------------------------------------------

export interface PlanRequest {
  sessionName?: string | null;
  sessionType?: SessionType | null;
  focus?: SessionFocus | null;
  rounds?: number;
  preWorkoutFeeling?: string | null;
  exerciseIds?: string[];
}

export const listSessions = (limit?: number, offset?: number) => {
  const q = new URLSearchParams();
  if (limit !== undefined) q.set("limit", String(limit));
  if (offset !== undefined) q.set("offset", String(offset));
  const suffix = q.size > 0 ? `?${q}` : "";
  return api.get<Paged<TrainingSession>>(`/api/v1/sessions${suffix}`);
};

export const getSession = (id: string) => api.get<TrainingSession>(`/api/v1/sessions/${id}`);

/** Plans without persisting — used to let the user regenerate before committing. */
export const previewPlan = (body: PlanRequest) =>
  api.post<PlannedSession>("/api/v1/sessions/plan/preview", body);

export const createPlan = (body: PlanRequest) =>
  api.post<CreatedPlan>("/api/v1/sessions/plan", body);

export const deleteSession = (id: string) =>
  api.delete<{ message: string }>(`/api/v1/sessions/${id}`);

export const startSession = (id: string) =>
  api.post<TrainingSession>(`/api/v1/sessions/${id}/start`);

export const completeSession = (
  id: string,
  body: { durationMinutes?: number | null; userNotes?: string | null },
) => api.post<TrainingSession>(`/api/v1/sessions/${id}/complete`, body);

export const logSet = (
  id: string,
  body: {
    sessionExerciseId: string;
    roundNumber: number;
    setNumber: number;
    repsCompleted?: number | null;
    weightUsedKg?: number | null;
    durationCompletedSeconds?: number | null;
    side?: "left" | "right" | "both" | null;
  },
) => api.post<unknown>(`/api/v1/sessions/${id}/sets`, body);

export const submitRpe = (
  id: string,
  body: { perceivedEffort: number; durationMinutes?: number | null },
) => api.post<TrainingSession>(`/api/v1/sessions/${id}/rpe`, body);

export const addSessionExercise = (
  id: string,
  body: {
    exerciseId: string;
    targetSets?: number;
    targetReps?: number | null;
    suggestedWeightKg?: number | null;
  },
) => api.post<unknown>(`/api/v1/sessions/${id}/exercises`, body);

export const removeSessionExercise = (id: string, exerciseId: string) =>
  api.delete<{ message: string }>(`/api/v1/sessions/${id}/exercises/${exerciseId}`);

export const reorderSessionExercises = (id: string, orderedIds: string[]) =>
  api.put<unknown>(`/api/v1/sessions/${id}/exercises/order`, { orderedIds });

export const getFeedback = (id: string) =>
  api.get<SessionFeedback>(`/api/v1/sessions/${id}/feedback`);

export const submitFeedback = (id: string, body: SessionFeedback) =>
  api.post<SessionFeedback>(`/api/v1/sessions/${id}/feedback`, body);

export const setSessionTags = (id: string, tags: string[]) =>
  api.put<Paged<Tag>>(`/api/v1/sessions/${id}/tags`, { tags });

export const removeSessionTag = (id: string, slug: string) =>
  api.delete<{ message: string }>(`/api/v1/sessions/${id}/tags/${slug}`);
